import { useMemo } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { EventsTimeline } from './events-timeline';
import { EventsFilterMenu } from './events-filter-menu';
import { EventsStatCard } from './events-stat-card';
import { EventsAlertCard } from './events-alert-card';
import type { NodeStatusEvent } from '@/types/workflow-types';
import type { NodeSummaryStats, AlertInfo } from '@/hooks/use-events-summary';

interface EventsNodeDetailProps {
  nodeId: string;
  nodeStats: NodeSummaryStats | undefined;
  modelFailures: AlertInfo[];
  retries: AlertInfo[];
  nodeStatuses: Record<string, NodeStatusEvent[]>;
  onBack?: () => void;
  isConnected?: boolean;
  isPaused?: boolean;
}

export function EventsNodeDetail({
  nodeId,
  nodeStats,
  modelFailures,
  retries,
  nodeStatuses,
  onBack,
  isConnected = false,
  isPaused = false,
}: EventsNodeDetailProps) {
  const nodeFailures = useMemo(
    () => modelFailures.filter((f) => f.nodeId === nodeId),
    [modelFailures, nodeId],
  );
  const nodeRetries = useMemo(
    () => retries.filter((r) => r.nodeId === nodeId),
    [retries, nodeId],
  );

  const totalFailures = nodeFailures.reduce((s, f) => s + f.count, 0);
  const totalRetries = nodeRetries.reduce((s, r) => s + r.count, 0);

  const hasAlerts = totalFailures > 0 || totalRetries > 0;

  const summaryHeader = (
    <>
      {nodeStats && (
        <div className='flex flex-col gap-4 p-4'>
          <div className='flex items-baseline justify-between'>
            <h3 className='text-sm font-semibold'>Node Summary</h3>
            <span className='text-sm text-muted-foreground'>
              {nodeStats.events} {nodeStats.events === 1 ? 'event' : 'events'}
            </span>
          </div>

          <div className='flex gap-2'>
            <EventsStatCard value={nodeStats.thoughts} label='Thought' />
            <EventsStatCard value={nodeStats.toolCalls} label='Tool call' />
            <EventsStatCard value={nodeStats.outputs} label='Output' />
          </div>

          {hasAlerts && (
            <div className='flex flex-col gap-2'>
              {totalFailures > 0 && (
                <EventsAlertCard
                  variant='error'
                  count={totalFailures}
                  label={`Model failure${totalFailures > 1 ? 's' : ''}`}
                  description={nodeFailures[0]?.description}
                />
              )}
              {totalRetries > 0 && (
                <EventsAlertCard
                  variant='warning'
                  count={totalRetries}
                  label={`Retr${totalRetries > 1 ? 'ies' : 'y'}`}
                  description={nodeRetries[0]?.description}
                />
              )}
            </div>
          )}
        </div>
      )}

      {nodeStats && hasAlerts && <Separator />}

      <div className='flex items-center justify-between px-4 py-2'>
        <h3 className='text-sm font-semibold'>Node Events</h3>
        <EventsFilterMenu />
      </div>
    </>
  );

  return (
    <div className='flex flex-col flex-1 min-h-0'>
      {onBack && (
        <>
          <div className='p-1.5 shrink-0'>
            <Button
              variant='ghost'
              size='sm'
              onClick={onBack}
              className='gap-2'
            >
              <ArrowLeft className='size-4' />
              <span>All Events</span>
            </Button>
          </div>
          <Separator />
        </>
      )}

      <EventsTimeline
        nodeStatuses={nodeStatuses}
        nodeId={nodeId}
        header={summaryHeader}
        isConnected={isConnected}
        isPaused={isPaused}
      />
    </div>
  );
}
